import {
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material'
import { IconSearch } from '@tabler/icons-react'
import useSWR from 'swr'
import axiosInstance from '../../../../utils/axiosInstance'
import { useSelector } from '../../../../store/store'
import { isEndCustomer } from '../../../../constants'

export interface JobsFilters {
  search: string
  customerId: string
  linkStatus: 'all' | 'linked' | 'unlinked'
}

export const defaultJobsFilters: JobsFilters = {
  search: '',
  customerId: '',
  linkStatus: 'all',
}

interface CustomerOption {
  value: string
  label: string
}

const linkStatusOptions: { value: JobsFilters['linkStatus']; label: string }[] = [
  { value: 'all', label: 'All jobs' },
  { value: 'linked', label: 'Linked to order' },
  { value: 'unlinked', label: 'Not linked' },
]

interface EnhancedTableToolbarProps {
  filters: JobsFilters
  onChange: (filters: JobsFilters) => void
}

const EnhancedTableToolbar = ({ filters, onChange }: EnhancedTableToolbarProps) => {
  const { role } = useSelector((state) => state.user.userInfo)
  // End customers only have their own jobs; resellers filter across their child customers.
  const showCustomerFilter = !isEndCustomer(role)

  const { data } = useSWR(showCustomerFilter ? '/jobs/filter-values' : null, (url) =>
    axiosInstance.get(url).then((res) => res.data)
  )
  const customers: CustomerOption[] = data?.customers || []

  const selectedCustomer =
    customers.find((customer) => customer.value === filters.customerId) || null
  const selectedLinkStatus =
    linkStatusOptions.find((option) => option.value === filters.linkStatus) || linkStatusOptions[0]

  const isFiltered =
    filters.search !== '' || filters.customerId !== '' || filters.linkStatus !== 'all'

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        flexWrap: 'wrap',
        gap: 2,
        py: 2,
      }}
    >
      <Typography sx={{ flex: '1 1 100%' }} variant="h6" id="tableTitle" component="div">
        Jobs
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', width: '100%' }}>
        <TextField
          size="small"
          placeholder={
            showCustomerFilter
              ? 'Search title, location, ID, customer…'
              : 'Search title, location, ID…'
          }
          value={filters.search}
          onChange={(event) => onChange({ ...filters, search: event.target.value })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconSearch size={18} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280, flex: '1 1 280px' }}
        />

        {showCustomerFilter && (
          <Autocomplete
            size="small"
            options={customers}
            getOptionLabel={(option) => option.label}
            value={selectedCustomer}
            onChange={(_, newValue) => onChange({ ...filters, customerId: newValue?.value || '' })}
            renderInput={(params) => (
              <TextField {...params} label="Customer" placeholder="All customers" />
            )}
            sx={{ minWidth: 220 }}
          />
        )}

        <Autocomplete
          size="small"
          disableClearable
          options={linkStatusOptions}
          getOptionLabel={(option) => option.label}
          value={selectedLinkStatus}
          onChange={(_, newValue) => onChange({ ...filters, linkStatus: newValue?.value || 'all' })}
          renderInput={(params) => <TextField {...params} label="Order link" />}
          sx={{ minWidth: 180 }}
        />

        {isFiltered && (
          <Button size="small" onClick={() => onChange(defaultJobsFilters)}>
            Clear filters
          </Button>
        )}
      </Box>
    </Toolbar>
  )
}

export default EnhancedTableToolbar
